import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Add,
  Warning,
  NotificationsActive,
  MoreVert,
  Edit,
  Delete,
  Visibility,
  CheckCircle,
  Error as ErrorIcon,
  Info,
  Search,
  ChevronRight,
  FilterList
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Snackbar, Alert } from '@mui/material';

const Alertas = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAlerta, setSelectedAlerta] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newAlertaDialog, setNewAlertaDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');
  
  const [filters, setFilters] = useState({
    tipo: 'todas',
    prioridad: 'todas',
    estado: 'todos'
  });
  
  const [formData, setFormData] = useState({
    tipo_alerta: 'sanitaria',
    titulo: '',
    descripcion: '',
    prioridad: 'media',
    fecha_alerta: new Date().toISOString().split('T')[0],
    estado: 'pendiente'
  });

  // Fetch alerts
  const { data: alertasData, isLoading } = useQuery('alertas', async () => {
    const response = await axios.get('/api/alertas');
    return response.data;
  });

  const showMessage = (msg) => { setSnackbarMsg(msg); setOpenSnackbar(true); };

  const createMutation = useMutation(async (data) => await axios.post('/api/alertas', data), {
    onSuccess: () => { 
      queryClient.invalidateQueries('alertas'); 
      queryClient.invalidateQueries('dashboard_stats');
      setNewAlertaDialog(false); 
      showMessage('🔔 Alerta registrada y notificada correctamente');
    },
    onError: () => { showMessage('❌ Error al registrar la alerta'); }
  });

  const updateMutation = useMutation(async (data) => await axios.put(`/api/alertas/${data.id}`, data), {
    onSuccess: () => { 
      queryClient.invalidateQueries('alertas'); 
      queryClient.invalidateQueries('dashboard_stats');
      setNewAlertaDialog(false); 
      showMessage('✅ Alerta actualizada');
    },
    onError: () => { showMessage('❌ Error al actualizar'); }
  });

  const deleteMutation = useMutation(async (id) => await axios.delete(`/api/alertas/${id}`), {
    onSuccess: () => { queryClient.invalidateQueries('alertas'); showMessage('🗑️ Alerta eliminada'); }
  });

  const handleMenuClick = (event, alerta) => { setAnchorEl(event.currentTarget); setSelectedAlerta(alerta); };
  const handleMenuClose = () => { setAnchorEl(null); };

  const handleViewAlerta = () => { setDialogOpen(true); handleMenuClose(); };
  const handleEditAlerta = () => { if (selectedAlerta) { setFormData(selectedAlerta); setIsEditing(true); setNewAlertaDialog(true); } handleMenuClose(); };
  const handleDeleteAlerta = () => { if (selectedAlerta) { if(window.confirm('¿Eliminar?')) deleteMutation.mutate(selectedAlerta.id); } handleMenuClose(); setDialogOpen(false); };
  const handleResolve = () => { if (selectedAlerta) { updateMutation.mutate({ ...selectedAlerta, estado: 'resuelta' }); } handleMenuClose(); setDialogOpen(false); };

  if (isLoading) return <Box sx={{ p: 4, mt: 8, textAlign: 'center' }}><Typography variant="h6">Cargando protocolo...</Typography></Box>;

  const alertas = Array.isArray(alertasData) ? alertasData : (alertasData?.alertas || []);
  
  const filteredAlertas = alertas.filter(a => {
    const matchesSearch = (a.titulo || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                         (a.descripcion || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTipo = filters.tipo === 'todas' || a.tipo_alerta === filters.tipo;
    const matchesPrioridad = filters.prioridad === 'todas' || a.prioridad === filters.prioridad;
    const matchesEstado = filters.estado === 'todos' || a.estado === filters.estado;
    
    return matchesSearch && matchesTipo && matchesPrioridad && matchesEstado;
  });

  const stats = {
    total: alertas.length,
    pendientes: alertas.filter(a => a.estado === 'pendiente' || a.estado === 'activa').length,
    criticas: alertas.filter(a => a.prioridad === 'alta' && (a.estado === 'pendiente' || a.estado === 'activa')).length,
    resueltas: alertas.filter(a => a.estado === 'resuelta').length
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: 8, bgcolor: 'var(--bg-app)', minHeight: '100vh' }}>
      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={() => setOpenSnackbar(false)}>
        <Alert severity={snackbarMsg.includes('❌') ? 'error' : 'success'} sx={{ width: '100%', borderRadius: '12px' }}>{snackbarMsg}</Alert>
      </Snackbar>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'var(--navy-main)' }}>Centro de Notificaciones</Typography>
          <Typography variant="body1" sx={{ color: 'var(--slate-light)' }}>Gestión de incidencias y avisos críticos.</Typography>
        </Box>
        <Button
          className="luxury-button luxury-button-primary"
          variant="contained"
          startIcon={<Add />}
          onClick={() => { setIsEditing(false); setFormData({ tipo_alerta: 'sanitaria', titulo: '', descripcion: '', prioridad: 'media', fecha_alerta: new Date().toISOString().split('T')[0], estado: 'pendiente' }); setNewAlertaDialog(true); }}
        >
          Nueva Alerta
        </Button>
      </Box>

      {/* KPI Ribbons */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Incidencias Totales', value: stats.total, color: 'var(--navy-main)', icon: <NotificationsActive /> },
          { label: 'Pendientes Activas', value: stats.pendientes, color: 'var(--alert-gold)', icon: <Warning /> },
          { label: 'Prioridad Crítica', value: stats.criticas, color: 'var(--alert-crimson)', icon: <ErrorIcon /> },
          { label: 'Casos Resueltos', value: stats.resueltas, color: 'var(--emerald-main)', icon: <CheckCircle /> },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card className="premium-card">
              <CardContent sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h3" sx={{ fontWeight: 800 }}>{s.value}</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'var(--slate-light)', textTransform: 'uppercase' }}>{s.label}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: `${s.color}15`, color: s.color }}>{s.icon}</Avatar>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Main Alerts Table */}
      <Card className="premium-card">
        <Box sx={{ p: 3, borderBottom: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Filtrar por título o contenido..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
              sx: { borderRadius: '12px', bgcolor: 'var(--slate-bg)' }
            }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Categoría</InputLabel>
                <Select value={filters.tipo} label="Categoría" onChange={(e) => setFilters({...filters, tipo: e.target.value})}>
                  <MenuItem value="todas">Todas</MenuItem>
                  <MenuItem value="sanitaria">Sanitaria</MenuItem>
                  <MenuItem value="administrativa">Administrativa</MenuItem>
                  <MenuItem value="operativa">Operativa</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Prioridad</InputLabel>
                <Select value={filters.prioridad} label="Prioridad" onChange={(e) => setFilters({...filters, prioridad: e.target.value})}>
                  <MenuItem value="todas">Todas</MenuItem>
                  <MenuItem value="alta">Alta</MenuItem>
                  <MenuItem value="media">Media</MenuItem>
                  <MenuItem value="baja">Baja</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select value={filters.estado} label="Estado" onChange={(e) => setFilters({...filters, estado: e.target.value})}>
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="pendiente">Pendiente</MenuItem>
                  <MenuItem value="resuelta">Resuelta</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'var(--slate-bg)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Tipo e Incidencia</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Prioridad</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Estado</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Fecha</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAlertas.map((alerta) => (
                <TableRow key={alerta.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: alerta.prioridad === 'alta' ? '#fff1f2' : '#f0f9ff' }}>
                        {alerta.tipo_alerta === 'sanitaria' ? <ErrorIcon color="error" /> : <Info color="primary" />}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{alerta.titulo}</Typography>
                        <Typography variant="caption" sx={{ color: 'var(--slate-light)' }}>{alerta.tipo_alerta}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip label={alerta.prioridad} size="small" sx={{ 
                      fontWeight: 800, fontSize: '10px',
                      bgcolor: alerta.prioridad === 'alta' ? 'var(--alert-crimson)' : 'var(--alert-gold)',
                      color: 'white'
                    }} />
                  </TableCell>
                  <TableCell>
                    <Chip label={alerta.estado} variant="outlined" size="small" sx={{ 
                      fontWeight: 700, 
                      borderColor: alerta.estado === 'pendiente' ? 'var(--alert-gold)' : 'var(--emerald-main)',
                      color: alerta.estado === 'pendiente' ? 'var(--alert-gold)' : 'var(--emerald-main)'
                    }} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{alerta.fecha_alerta}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={(e) => handleMenuClick(e, alerta)}><MoreVert /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={newAlertaDialog} onClose={() => setNewAlertaDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <DialogTitle sx={{ fontWeight: 800 }}>{isEditing ? 'Editar Alerta' : 'Nueva Alerta'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Categoría</InputLabel>
                <Select value={formData.tipo_alerta} label="Categoría" onChange={(e) => setFormData({...formData, tipo_alerta: e.target.value})}>
                  <MenuItem value="sanitaria">Emergencia Sanitaria</MenuItem>
                  <MenuItem value="administrativa">Trámite Externo</MenuItem>
                  <MenuItem value="operativa">Aviso Logístico</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Prioridad</InputLabel>
                <Select value={formData.prioridad} label="Prioridad" onChange={(e) => setFormData({...formData, prioridad: e.target.value})}>
                  <MenuItem value="alta">ALTA / CRÍTICA</MenuItem>
                  <MenuItem value="media">MEDIA</MenuItem>
                  <MenuItem value="baja">BAJA</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Título" value={formData.titulo} onChange={(e) => setFormData({...formData, titulo: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={4} label="Descripción" value={formData.descripcion} onChange={(e) => setFormData({...formData, descripcion: e.target.value})} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setNewAlertaDialog(false)}>Cancelar</Button>
          <Button variant="contained" className="luxury-button luxury-button-primary" onClick={() => isEditing ? updateMutation.mutate(formData) : createMutation.mutate(formData)}>
            {isEditing ? 'Actualizar' : 'Notificar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '24px' } }}>
        <Box sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: selectedAlerta?.prioridad === 'alta' ? 'var(--alert-crimson)' : 'var(--alert-gold)', color: 'white' }}>
              <Warning fontSize="large" />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>{selectedAlerta?.titulo}</Typography>
              <Chip label={selectedAlerta?.tipo_alerta} size="small" variant="outlined" sx={{ mt: 0.5 }} />
            </Box>
          </Box>
          <Typography variant="body1" sx={{ mb: 3 }}>{selectedAlerta?.descripcion}</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button fullWidth variant="outlined" onClick={() => setDialogOpen(false)}>Cerrar</Button>
            {selectedAlerta?.estado === 'pendiente' && (
              <Button fullWidth variant="contained" className="luxury-button luxury-button-primary" onClick={handleResolve}>Resolver</Button>
            )}
          </Box>
        </Box>
      </Dialog>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose} PaperProps={{ sx: { borderRadius: '12px', minWidth: '200px' } }}>
        <MenuItem onClick={handleViewAlerta} sx={{ gap: 1.5 }}><Visibility fontSize="small" /> Ver Detalles</MenuItem>
        <MenuItem onClick={handleEditAlerta} sx={{ gap: 1.5 }}><Edit fontSize="small" /> Editar</MenuItem>
        <MenuItem onClick={handleResolve} sx={{ gap: 1.5, color: 'var(--emerald-main)' }}><CheckCircle fontSize="small" /> Resolver</MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteAlerta} sx={{ gap: 1.5, color: 'var(--alert-crimson)' }}><Delete fontSize="small" /> Eliminar</MenuItem>
      </Menu>
    </Box>
  );
};

export default Alertas;
